import { BookOpen, Copy, History, MoreHorizontal, Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button, Tag, Typography } from '~/components'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '~/components/common/dropdowns/DropdownMenu'
import { useModal } from '~/components/modals/CurieModal'
import {
  ScheduledTask,
  useCancelScheduledTask,
  useScheduledTasks,
  useUpdateScheduledTask
} from '~/services/scheduledTaskService'
import { BetterToast } from '~/utils/toast'
import { CreateScheduledTaskModal } from './components/CreateScheduledTaskModal'
import { describeSchedule, ScheduledTaskDetailModal } from './components/ScheduledTaskDetailModal'
import { ScheduledTaskInstructionsModal } from './components/ScheduledTaskInstructionsModal'

const STATUS_TO_TAG_VARIANT: Record<
  ScheduledTask['status'],
  'success' | 'warning' | 'error' | 'info'
> = {
  ACTIVE: 'success',
  PAUSED: 'warning',
  ERRORED: 'error',
  COMPLETED: 'info',
  CANCELLED: 'info'
}

const SCHEDULED_TASKS_TABLE_GRID = 'grid-cols-[152px_minmax(144px,1fr)_120px_64px_64px]'
const SCHEDULED_TASKS_TABLE_COLUMNS: ReadonlyArray<{
  label: string
  className?: string
}> = [
  { label: 'Name' },
  { label: 'Prompt' },
  { label: 'Schedule' },
  { label: 'Status' },
  { label: 'Actions', className: 'text-center' }
]

export default function ScheduledTasksSettingsTab() {
  const { data, isLoading, error } = useScheduledTasks()
  const update = useUpdateScheduledTask()
  const cancel = useCancelScheduledTask()
  const [pendingUuids, setPendingUuids] = useState<Set<string>>(() => new Set())
  const [viewingUuid, setViewingUuid] = useState<string | null>(null)
  const clearViewingUuidTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const detailModal = useModal()
  const instructionModal = useModal()
  const createModal = useModal()

  const schedules = data?.schedules ?? []

  const cancelPendingDetailClear = () => {
    if (!clearViewingUuidTimeoutRef.current) return
    clearTimeout(clearViewingUuidTimeoutRef.current)
    clearViewingUuidTimeoutRef.current = null
  }

  useEffect(() => {
    return () => {
      if (!clearViewingUuidTimeoutRef.current) return
      clearTimeout(clearViewingUuidTimeoutRef.current)
      clearViewingUuidTimeoutRef.current = null
    }
  }, [])

  const onView = (s: ScheduledTask) => {
    cancelPendingDetailClear()
    setViewingUuid(s.uuid)
    detailModal.open()
  }

  const onCloseDetail = () => {
    detailModal.close()
    // Defer clearing so the modal closes smoothly before the data unmounts.
    cancelPendingDetailClear()
    clearViewingUuidTimeoutRef.current = setTimeout(() => {
      setViewingUuid(null)
      clearViewingUuidTimeoutRef.current = null
    }, 200)
  }

  const onPauseResume = async (s: ScheduledTask) => {
    setPendingUuids(current => new Set(current).add(s.uuid))
    try {
      await update.mutateAsync({
        uuid: s.uuid,
        payload: { action: s.status === 'ACTIVE' ? 'pause' : 'resume' }
      })
      BetterToast.success(s.status === 'ACTIVE' ? 'Schedule paused' : 'Schedule resumed')
    } catch (ex) {
      BetterToast.error(ex instanceof Error ? ex.message : 'Failed to update schedule')
    } finally {
      setPendingUuids(current => {
        const next = new Set(current)
        next.delete(s.uuid)
        return next
      })
    }
  }

  const onCancel = async (s: ScheduledTask) => {
    if (!window.confirm(`Cancel schedule "${s.name}"? In-flight tasks will not be stopped.`)) {
      return
    }
    setPendingUuids(current => new Set(current).add(s.uuid))
    try {
      await cancel.mutateAsync({ uuid: s.uuid })
      BetterToast.success('Schedule cancelled')
    } catch (ex) {
      BetterToast.error(ex instanceof Error ? ex.message : 'Failed to cancel schedule')
    } finally {
      setPendingUuids(current => {
        const next = new Set(current)
        next.delete(s.uuid)
        return next
      })
    }
  }

  return (
    <div className='w-full flex flex-col gap-6 px-4' data-testid='scheduled-tasks-form'>
      {error ? (
        <Typography fontFamily='geist' fontSize='bodyMd' className='text-tokens-status-error-text'>
          {`Failed to load scheduled tasks: ${error.message}`}
        </Typography>
      ) : null}

      {isLoading ? (
        <Typography fontFamily='geist' fontSize='bodyMd' className='select-none'>
          Loading…
        </Typography>
      ) : schedules.length === 0 ? (
        <ScheduledTasksEmptyState
          onScheduleInChat={createModal.open}
          onViewInstruction={instructionModal.open}
        />
      ) : (
        <ScheduledTasksPopulatedState
          schedules={schedules}
          pendingUuids={pendingUuids}
          onCreate={createModal.open}
          onView={onView}
          onPauseResume={onPauseResume}
          onCancel={onCancel}
          onViewInstruction={instructionModal.open}
        />
      )}

      <ScheduledTaskDetailModal
        uuid={viewingUuid}
        isOpen={detailModal.isOpen}
        onClose={onCloseDetail}
      />
      <ScheduledTaskInstructionsModal
        isOpen={instructionModal.isOpen}
        onClose={instructionModal.close}
      />
      <CreateScheduledTaskModal isOpen={createModal.isOpen} onClose={createModal.close} />
    </div>
  )
}

function ScheduledTasksPopulatedState({
  schedules,
  pendingUuids,
  onCreate,
  onView,
  onPauseResume,
  onCancel,
  onViewInstruction
}: {
  schedules: ScheduledTask[]
  pendingUuids: ReadonlySet<string>
  onCreate: () => void
  onView: (schedule: ScheduledTask) => void
  onPauseResume: (schedule: ScheduledTask) => void
  onCancel: (schedule: ScheduledTask) => void
  onViewInstruction: () => void
}) {
  return (
    <div className='flex w-full flex-col items-center gap-6 overflow-hidden'>
      <section
        className='flex w-full max-w-[919px] items-center gap-3 rounded-[16px] bg-tokens-neutral-fill-fill-4 p-4'
        data-testid='scheduled-tasks-populated-header'
        aria-label='Automated tasks'
      >
        <div
          className='flex min-w-[152px] flex-1 items-center gap-3'
          data-testid='scheduled-tasks-populated-title'
        >
          <div className='flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-tokens-primary-4/10'>
            <History className='h-3.5 w-3.5 text-tokens-status-link-1' aria-hidden='true' />
          </div>
          <Typography
            fontFamily='geistMono'
            fontSize='labelMd'
            fontWeight='semibold'
            className='select-none truncate text-tokens-neutral-text-secondary'
          >
            Automated Tasks
          </Typography>
        </div>
        <div className='flex shrink-0 items-center gap-1'>
          <Button
            variant='outlined'
            onClick={onCreate}
            leftIcon={<Plus className='h-4 w-4' aria-hidden='true' />}
            className='h-9 text-sm'
          >
            New Scheduled Task
          </Button>
          <button
            type='button'
            onClick={onViewInstruction}
            aria-label='View scheduling instructions'
            className='inline-flex h-9 w-9 items-center justify-center rounded-[12px] text-tokens-neutral-icon-default transition-colors hover:bg-tokens-neutral-fill-fill-6'
          >
            <BookOpen className='h-4 w-4' aria-hidden='true' />
          </button>
        </div>
      </section>

      <div className='w-full max-w-[919px]' data-testid='scheduled-tasks-table'>
        <div
          className={`grid min-h-10 ${SCHEDULED_TASKS_TABLE_GRID} items-center gap-6 px-4 pr-8`}
          data-testid='scheduled-tasks-table-header'
        >
          {SCHEDULED_TASKS_TABLE_COLUMNS.map(column => (
            <Typography
              key={column.label}
              fontFamily='geistMono'
              fontSize='labelMd'
              fontWeight='semibold'
              className={`select-none text-tokens-neutral-text-secondary ${column.className ?? ''}`}
            >
              {column.label}
            </Typography>
          ))}
        </div>
        <div className='flex flex-col gap-3' data-testid='scheduled-tasks-table-body'>
          {schedules.map(schedule => (
            <div
              key={schedule.uuid}
              className={`grid min-h-8 ${SCHEDULED_TASKS_TABLE_GRID} items-center gap-6 rounded-[12px] px-4 pr-8 transition-colors hover:bg-tokens-neutral-fill-fill-4`}
              data-testid='scheduled-tasks-table-row'
            >
              <ScheduleNameButton schedule={schedule} />
              <div className='truncate' title={schedule.description || schedule.name}>
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='medium'
                  className='truncate text-tokens-neutral-text-primary'
                >
                  {schedule.description || schedule.name}
                </Typography>
              </div>
              <div className='truncate' title={describeSchedule(schedule)}>
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='medium'
                  className='truncate text-tokens-neutral-text-primary'
                >
                  {describeSchedule(schedule)}
                </Typography>
              </div>
              <Tag
                variant={STATUS_TO_TAG_VARIANT[schedule.status]}
                className='w-fit justify-center px-2 py-1 text-[10px]'
              >
                {schedule.status}
              </Tag>
              <ScheduledTaskActionsMenu
                schedule={schedule}
                isPending={pendingUuids.has(schedule.uuid)}
                onView={onView}
                onPauseResume={onPauseResume}
                onCancel={onCancel}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function ScheduleNameButton({ schedule }: { schedule: ScheduledTask }) {
  return (
    <button
      type='button'
      className='group flex w-full min-w-0 items-center gap-1.5 text-left font-geist text-[12px] font-bold leading-4 text-tokens-neutral-text-primary hover:text-tokens-status-link-1 focus-visible:outline-none'
      aria-label={`Copy schedule ID for ${schedule.name}`}
      title={schedule.uuid}
      onClick={() => {
        void navigator.clipboard.writeText(schedule.uuid).then(
          () => BetterToast.success('Schedule ID copied'),
          () => BetterToast.error('Failed to copy ID')
        )
      }}
    >
      <span className='block w-[120px] truncate'>{schedule.name}</span>
      <Copy
        className='h-3.5 w-3.5 shrink-0 text-tokens-neutral-icon-default transition-colors group-hover:text-tokens-neutral-icon-hover group-focus-visible:text-tokens-neutral-icon-hover'
        aria-hidden='true'
      />
    </button>
  )
}

function ScheduledTaskActionsMenu({
  schedule,
  isPending,
  onView,
  onPauseResume,
  onCancel
}: {
  schedule: ScheduledTask
  isPending: boolean
  onView: (schedule: ScheduledTask) => void
  onPauseResume: (schedule: ScheduledTask) => void
  onCancel: (schedule: ScheduledTask) => void
}) {
  const canPauseOrResume = schedule.status === 'ACTIVE' || schedule.status === 'PAUSED'
  const canCancel = schedule.status !== 'CANCELLED' && schedule.status !== 'COMPLETED'
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className='flex justify-center'>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type='button'
            aria-label={`Actions for ${schedule.name}`}
            onClick={() => setIsOpen(current => !current)}
            className='inline-flex h-9 w-9 items-center justify-center rounded-[8px] text-tokens-neutral-icon-default transition-colors hover:bg-tokens-neutral-fill-fill-6'
          >
            <MoreHorizontal className='h-5 w-5' aria-hidden='true' />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='w-[154px]'>
          <DropdownMenuItem
            onClick={() => {
              setIsOpen(false)
              onView(schedule)
            }}
          >
            <Typography fontFamily='geist' fontSize='bodyMd' fontWeight='medium'>
              Task Details
            </Typography>
          </DropdownMenuItem>
          {canPauseOrResume ? (
            <DropdownMenuItem
              disabled={isPending}
              onClick={() => {
                if (!isPending) {
                  setIsOpen(false)
                  onPauseResume(schedule)
                }
              }}
            >
              <Typography fontFamily='geist' fontSize='bodyMd' fontWeight='medium'>
                {schedule.status === 'ACTIVE' ? 'Pause Task' : 'Resume Task'}
              </Typography>
            </DropdownMenuItem>
          ) : null}
          {canCancel ? (
            <>
              <DropdownMenuSeparator className='my-1' />
              <DropdownMenuItem
                disabled={isPending}
                onClick={() => {
                  if (!isPending) {
                    setIsOpen(false)
                    onCancel(schedule)
                  }
                }}
                className='text-tokens-status-error-text'
              >
                <Typography fontFamily='geist' fontSize='bodyMd' fontWeight='medium'>
                  Cancel Task
                </Typography>
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function ScheduledTasksEmptyState({
  onScheduleInChat,
  onViewInstruction
}: {
  onScheduleInChat: () => void
  onViewInstruction: () => void
}) {
  return (
    <div
      className='flex min-h-[420px] w-full flex-col items-center justify-center gap-6 px-4 py-16 text-center'
      data-testid='scheduled-tasks-empty'
    >
      <History
        className='h-12 w-12 text-tokens-neutral-text-quaternary/30'
        strokeWidth={1.75}
        aria-hidden='true'
      />
      <Typography
        fontFamily='geist'
        fontSize='bodyMd'
        fontWeight='medium'
        className='select-none text-tokens-neutral-text-quaternary/45'
      >
        Once you create your first schedule from chat, it will show up here.
      </Typography>
      <div className='flex flex-col items-center gap-3'>
        <Button
          variant='outlined'
          onClick={onScheduleInChat}
          leftIcon={<Plus className='h-4 w-4' aria-hidden='true' />}
          className='h-10 text-sm'
        >
          Add new scheduled task
        </Button>
        <Button
          variant='borderless'
          onClick={onViewInstruction}
          leftIcon={<BookOpen className='h-4 w-4' aria-hidden='true' />}
          className='h-9 text-sm text-tokens-status-link-1 hover:text-tokens-status-link-1'
        >
          View Instruction
        </Button>
      </div>
    </div>
  )
}
